import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChangePassword, useUpdateMe } from '../../api/me';
import { useAuth } from '../../auth/useAuth';
import { Avatar } from '../../components/Avatar';
import { Page, PageHeader } from '../../components/Layout';
import { Spinner } from '../../components/Spinner';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../lib/api';

/**
 * The operator's own account.
 *
 * Deliberately not part of /users: everything an admin does to somebody
 * else's account is an administrative act with an audit entry behind it,
 * and acting on yourself is not. It is also the only way an administrator
 * created by `scripts/create-admin.js` can replace the password they were
 * handed at the command line.
 */
export function AccountPage() {
  const { user, refreshUser, logout } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();

  const updateMe = useUpdateMe();
  const changePassword = useChangePassword();

  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatarUrl ?? '');
  const [phone, setPhone] = useState(user?.phone ?? '');
  const [timezone, setTimezone] = useState(user?.timezone ?? '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');

  if (!user) return null;

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    // The API rejects an empty PATCH rather than treating it as a no-op, so
    // only send what actually moved.
    const patch: Record<string, string> = {};
    if (fullName.trim() !== user.fullName) patch.fullName = fullName.trim();
    if (avatarUrl.trim() !== (user.avatarUrl ?? '')) patch.avatarUrl = avatarUrl.trim();
    if (phone.trim() !== (user.phone ?? '')) patch.phone = phone.trim();
    if (timezone.trim() !== user.timezone) patch.timezone = timezone.trim();
    if (Object.keys(patch).length === 0) {
      notify('Nothing to save');
      return;
    }
    try {
      await updateMe.mutateAsync(patch);
      await refreshUser();
      notify('Saved', 'success');
    } catch (err) {
      notify(err instanceof ApiError ? err.message : 'Could not save.', 'error');
    }
  };

  /**
   * A password change bumps `token_version`, which ends every session for
   * this account — including this one. There is no token left to make the
   * next request with, so sign out here rather than letting the console
   * start failing one call at a time.
   */
  const savePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await changePassword.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      logout();
      notify('Password changed. Sign in again with the new one.', 'success');
      navigate('/login', { replace: true });
    } catch (err) {
      notify(
        err instanceof ApiError ? err.message : 'Could not change the password.',
        'error',
      );
    }
  };

  return (
    <Page>
      <PageHeader
        title="Your account"
        description="Your own details. Everything you do to other accounts lives under People."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <form onSubmit={saveProfile} className="panel space-y-4 p-4">
          <h2 className="text-sm font-semibold text-ink">Who you are</h2>

          <div className="flex items-center gap-3">
            <Avatar name={fullName || user.fullName} src={avatarUrl.trim() || undefined} />
            <label className="block min-w-0 flex-1">
              <span className="label">Picture URL</span>
              <input
                className="input"
                type="url"
                value={avatarUrl}
                onChange={(e) => setAvatarUrl(e.target.value)}
                placeholder="https://example.com/you.jpg"
              />
            </label>
          </div>

          <label className="block">
            <span className="label">Full name</span>
            <input
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="label">Phone</span>
              <input
                className="input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+20 123 456 7890"
              />
            </label>
            <label className="block">
              <span className="label">Timezone</span>
              <input
                className="input"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              />
            </label>
          </div>

          <label className="block">
            <span className="label">Email</span>
            <input className="input" value={user.email} disabled />
            <span className="mt-1 block text-[0.6875rem] text-ink-3">
              An address is changed from the user's record, so the change is audited.
            </span>
          </label>

          <button type="submit" className="btn btn-primary" disabled={updateMe.isPending}>
            {updateMe.isPending && <Spinner />}
            {updateMe.isPending ? 'Saving…' : 'Save'}
          </button>
        </form>

        <form onSubmit={savePassword} className="panel space-y-4 p-4">
          <div>
            <h2 className="text-sm font-semibold text-ink">Password</h2>
            <p className="mt-0.5 text-xs text-ink-3">
              Changing it ends every session on this account, including this one.
            </p>
          </div>

          <label className="block">
            <span className="label">Current password</span>
            <input
              className="input"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </label>

          <label className="block">
            <span className="label">New password</span>
            <input
              className="input"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <span className="mt-1 block text-[0.6875rem] text-ink-3">
              At least 8 characters, and different from the current one.
            </span>
          </label>

          <button
            type="submit"
            className="btn btn-primary"
            disabled={changePassword.isPending}
          >
            {changePassword.isPending && <Spinner />}
            {changePassword.isPending ? 'Changing…' : 'Change password and sign out'}
          </button>
        </form>
      </div>
    </Page>
  );
}
